import { injectable } from "inversify";
import { rootContainer, TYPE } from "../container/root";
import { IApiResponse, IHttpService } from "../http-service";
import { INotificationTemplate } from "../../pages/emails/emailContentTypes";
import {WithOld} from "../../stores/types";

export interface ISupportedIntegration {
    isActive: boolean,
    serviceName: string,
    serviceCode: string
}
export interface IEventsAndVariables {
    integrations: ISupportedIntegration[],
    categories: [{category: EMAIL_CATEGORY, list:[INotificationEvent]}],
    channels: [{value: string, label: string}]
}

export interface IEmailCustomizationSettings {
	default_sender_address:string;
	sender_name: string;
	reply_to_address: string;
}

export interface IEmailCustomizationData {
	emailCustomization:IEmailCustomization;
	settings: IEmailCustomizationSettings;
}
export interface IEmailCustomization {
	sender_address?: string;
	logo?: string;
	bg_color?: string;
	text_color?: string;
	link_color?: string;
	logo_align?: string;
	btn_bg_color?: string;
}
interface INewLogoData {
	base64_content: string;
	type: string;
}
export interface IEmailCustomizationChange extends Partial<IEmailCustomization> {
	_delete_logo?: boolean;
	_new_logo_data?: INewLogoData;
}
export interface INotificationEvent {
	eventId: string;
	title: string;
	description?:string;
	scope: string;
	category: EMAIL_CATEGORY;
	content: string;
	index: number;
	_deleted: boolean;
	setting: { value: string; label: string };
	notification?: INotificationTemplate;
}

export interface ISendTestNotficationData {
    eventId: string,
    toAddresses: string[],
    serviceCode: string,
    locale: string,
}

export enum EMAIL_CATEGORY {
    returns_email = 'Returns Email',
    tracking_order_email = 'Order Tracking Emails',
}

export interface INotificationDescription {
    category: string,
    variables: INotificationItemDescription[],
    events: INotificationItemDescription[]
}
export interface INotificationItemDescription {
    title: string,
    helpText: string
}

@injectable()
class EmailContentService {
    private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);

    async getNotificationEventsList(): Promise<IEventsAndVariables> {
        const res = await this.httpService.get<IApiResponse<IEventsAndVariables>>(`/rms-api/email/content`);
        return res.payload;
    }

    async getVariablesAndEventsDescription(): Promise<INotificationDescription[]> {
        const res = await this.httpService.get<IApiResponse<INotificationDescription[]>>(`/rms-api/email/variables-and-events`);
        return res.payload;
    }

    async sendTestEvent(testData: ISendTestNotficationData): Promise<string> {
        const res = await this.httpService.post<IApiResponse<{message: string}>>(`/rms-api/email/test`, testData);
        return res.payload.message;
    }

    async updateEvent(event: WithOld<INotificationEvent>): Promise<string> {
        const res = await this.httpService.post<IApiResponse<{message: string}>>(`/rms-api/email/update`, event);
        return res.payload.message;
    }

	async getEventNotification(eventId?: string): Promise<INotificationTemplate> {
		const res = await this.httpService.get<IApiResponse<INotificationTemplate>>(
			`/rms-api/email/template/${eventId}`
		);
		return res.payload;
	}

	async createNotification(
		eventId: string | undefined,
		event: INotificationTemplate
	): Promise<INotificationTemplate> {
		const res = await this.httpService.post<IApiResponse<INotificationTemplate>>(
			`/rms-api/email/template/${eventId}`,
			event
		);
		return res.payload;
	}

	async updateNotification(
		eventId: string | undefined,
		notificationId: string,
		event: WithOld<INotificationTemplate>
	): Promise<INotificationTemplate> {
		const res = await this.httpService.put<IApiResponse<INotificationTemplate>>(
			`/rms-api/email/template/${eventId}/${notificationId}`,
			event
		);
		return res.payload;
	}

	async getEmailCustomization(): Promise<IEmailCustomizationData> {
		const res = await this.httpService.get<IApiResponse<IEmailCustomizationData>>(`/rms-api/email/customization`);
		return res.payload;
	}
	
	async updateEmailCustomization(data: WithOld<IEmailCustomization>): Promise<IEmailCustomization> {
		const res = await this.httpService.put<IApiResponse<IEmailCustomization>>(`/rms-api/email/customization`, data);
		return res.payload;
	}
}

export default EmailContentService;
